/**
 * Tabela no celular vira lista de cartões (design/tabelas.css) — nunca rola para o lado, que
 * esconde colunas. No cartão, cada valor precisa dizer o que é: este módulo copia o texto do
 * cabeçalho de cada coluna para `data-rotulo` nas células, e o CSS mostra esse rótulo em cima do
 * valor.
 *
 * Um só observador para o app inteiro: toda tabela das telas de gestão ganha o rótulo sem cada
 * tela precisar repetir o nome da coluna em cada <td>. Só trabalha enquanto a tela está na
 * largura de celular; no monitor a tabela é tabela e o atributo nem é lido.
 */

/** As tabelas que viram cartão no celular. Mesma lista de design/tabelas.css. */
export const TABELAS_QUE_VIRAM_CARTAO =
  '.cv-tabela, .ac-tabela, .dv-tabela, .ql-tabela, .ig-tabela, .sd-tabela, .vd-tabela table, .pr-tabela table';

const CELULAR = '(max-width: 767px)';

/** Um rótulo por coluna: o `th` que junta N colunas rotula a 1ª e deixa as outras sem nome. */
export function rotulosDasColunas(
  cabecalhos: readonly { readonly texto: string; readonly colSpan: number }[],
): string[] {
  const rotulos: string[] = [];
  for (const th of cabecalhos) {
    rotulos.push(th.texto.replace(/\s+/g, ' ').trim());
    for (let i = 1; i < th.colSpan; i++) rotulos.push('');
  }
  return rotulos;
}

/**
 * O rótulo de cada célula de uma linha, pela coluna onde ela começa. Célula que junta colunas
 * (aviso de "nada aqui", total) não tem um rótulo só: fica sem.
 */
export function rotulosDaLinha(rotulos: readonly string[], colSpans: readonly number[]): string[] {
  let coluna = 0;
  return colSpans.map((span) => {
    const rotulo = span > 1 ? '' : (rotulos[coluna] ?? '');
    coluna += span;
    return rotulo;
  });
}

export function rotularTabela(tabela: HTMLTableElement): void {
  const cabecalho = tabela.tHead;
  const linha = cabecalho?.rows[cabecalho.rows.length - 1];
  if (!linha) return;
  const rotulos = rotulosDasColunas(
    Array.from(linha.cells).map((th) => ({
      texto: th.getAttribute('aria-label') ?? th.textContent ?? '',
      colSpan: th.colSpan,
    })),
  );
  const corpos = [...Array.from(tabela.tBodies), ...(tabela.tFoot ? [tabela.tFoot] : [])];
  for (const corpo of corpos) {
    for (const tr of Array.from(corpo.rows)) {
      const celulas = Array.from(tr.cells);
      const daLinha = rotulosDaLinha(
        rotulos,
        celulas.map((c) => c.colSpan),
      );
      celulas.forEach((celula, i) => {
        const rotulo = daLinha[i] ?? '';
        if (rotulo === '') celula.removeAttribute('data-rotulo');
        else if (celula.getAttribute('data-rotulo') !== rotulo)
          celula.setAttribute('data-rotulo', rotulo);
      });
    }
  }
}

/** Liga o observador. Devolve a função que desliga (para teste). */
export function rotularTabelasDoApp(raiz: HTMLElement = document.body): () => void {
  const celular = window.matchMedia(CELULAR);
  const pendentes = new Set<HTMLTableElement>();
  let tudo = true;
  let agendado = false;

  const passar = () => {
    agendado = false;
    if (!celular.matches) return;
    const tabelas = tudo
      ? Array.from(raiz.querySelectorAll<HTMLTableElement>(TABELAS_QUE_VIRAM_CARTAO))
      : [...pendentes];
    tudo = false;
    pendentes.clear();
    for (const t of tabelas) if (t.isConnected) rotularTabela(t);
  };
  const agendar = () => {
    if (agendado) return;
    agendado = true;
    requestAnimationFrame(passar);
  };

  const observador = new MutationObserver((registros) => {
    if (!celular.matches) return;
    for (const r of registros) {
      const alvo = r.target instanceof Element ? r.target : r.target.parentElement;
      const tabela = alvo?.closest('table');
      if (tabela instanceof HTMLTableElement && tabela.matches(TABELAS_QUE_VIRAM_CARTAO)) {
        pendentes.add(tabela);
      }
      for (const no of Array.from(r.addedNodes)) {
        if (!(no instanceof Element)) continue;
        if (no instanceof HTMLTableElement && no.matches(TABELAS_QUE_VIRAM_CARTAO))
          pendentes.add(no);
        for (const t of Array.from(no.querySelectorAll<HTMLTableElement>(TABELAS_QUE_VIRAM_CARTAO)))
          pendentes.add(t);
      }
    }
    if (pendentes.size > 0) agendar();
  });
  observador.observe(raiz, { childList: true, subtree: true, characterData: true });

  const aoMudarLargura = () => {
    tudo = true;
    agendar();
  };
  celular.addEventListener('change', aoMudarLargura);
  agendar();

  return () => {
    observador.disconnect();
    celular.removeEventListener('change', aoMudarLargura);
  };
}
